import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { almacen, newOperationId } from "../api";
import {
  estadoDeCuarentena,
  liberarPideLote,
  opinionDeLaCaja,
  origenDeCuarentena,
  partirCuarentena,
  type EntradaDeCuarentena,
} from "../botica";
import { CampoDeVencimiento } from "../CampoDeVencimiento";
import {
  Boton,
  Cabecera,
  Cargando,
  Fallo,
  Modal,
  Pastilla,
  Rotulo,
  Tarjeta,
  Vacio,
} from "../componentes";
import { cantidad, dia, hora } from "../formato";
import { fechaDeCalendario } from "../vencimiento";

/**
 * Lo que el cliente devolvió en la botica, esperando que alguien lo revise.
 *
 * Con el lote en la venta, nada devuelto vuelve al estante desde la caja: un
 * frasco que salió del local no se sabe cómo se guardó. Espera aquí, fuera del
 * stock —no se vende ni se cuenta—, hasta que quien firma lo descartado lo
 * libere o lo dé de baja. Ver docs/15 §7 sexies (migración 240).
 *
 * Desde la migración 269 también espera aquí lo anulado después de cobrar y el
 * sobrante de un conteo que no traía lote ni vencimiento: a ese, liberarlo le
 * pide los dos, porque un lote sin fecha se vendería para siempre.
 */
export function Cuarentena({ puedeDecidir }: { puedeDecidir: boolean }) {
  const consulta = useQuery({
    queryKey: ["cuarentena"],
    queryFn: almacen.quarantine,
  });
  const [decidiendo, setDecidiendo] = useState<{
    entrada: EntradaDeCuarentena;
    decision: "release" | "write-off";
  } | null>(null);

  const { pendientes, decididas } = partirCuarentena(consulta.data ?? []);

  return (
    <>
      <Cabecera
        titulo="Cuarentena"
        subtitulo="Lo devuelto, lo anulado y lo que sobró en un conteo, fuera del stock hasta que alguien lo revise"
        ayuda="Lo que un cliente devuelve no vuelve a la venta desde la caja. Espera aquí hasta que quien responde por los lotes lo mire: si está bien, lo libera al estante; si no, lo da de baja y el kardex lo cuenta."
      />

      <div className="cuerpo">
        {consulta.isError ? (
          <Fallo
            error={consulta.error}
            onReintentar={() => consulta.refetch()}
          />
        ) : null}

        {!puedeDecidir && pendientes.length > 0 ? (
          <p className="aviso-caja">
            Liberar o dar de baja lo decide quien firma lo que se descarta, como
            el químico farmacéutico. Aquí lo puedes ver, no decidirlo.
          </p>
        ) : null}

        <div className="tabla-envoltura">
          {consulta.isLoading ? (
            <Cargando que="la cuarentena" />
          ) : pendientes.length === 0 ? (
            <Vacio
              titulo="Nada esperando revisión"
              mensaje="Cuando un cliente devuelva algo en la caja, aparece aquí hasta que alguien decida si vuelve al estante o se da de baja."
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Lote</th>
                  <th className="numero">Cantidad</th>
                  <th>Lo que dijo la caja</th>
                  <th>Devuelto</th>
                  {puedeDecidir ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {pendientes.map((entrada) => (
                  <tr key={entrada.id}>
                    <td>
                      <div className="celda-nombre__texto">
                        <strong>{entrada.productName}</strong>
                        <span>
                          {origenDeCuarentena(entrada.origin)} ·{" "}
                          {entrada.supplyName}
                          {entrada.orderNumber === null
                            ? ""
                            : ` · pedido #${entrada.orderNumber}`}
                        </span>
                      </div>
                    </td>
                    <td className="mono">
                      {entrada.lotCode ?? "Sin lote"}
                      {entrada.expiresOn === null ? null : (
                        <span className="apagado">
                          {" "}
                          · vence {fechaDeCalendario(entrada.expiresOn)}
                        </span>
                      )}
                    </td>
                    <td className="numero">
                      {cantidad(entrada.quantity)} {entrada.unit}
                    </td>
                    <td>
                      <Pastilla
                        texto={opinionDeLaCaja(entrada.cashierSaysRestock)}
                        tono={
                          entrada.cashierSaysRestock === false
                            ? "aviso"
                            : "neutro"
                        }
                      />
                    </td>
                    <td className="apagado mono">
                      {dia(entrada.createdAt)} {hora(entrada.createdAt)}
                    </td>
                    {puedeDecidir ? (
                      <td>
                        <div className="formulario__fila">
                          <Boton
                            onClick={() =>
                              setDecidiendo({ entrada, decision: "release" })
                            }
                          >
                            Liberar al estante
                          </Boton>
                          <Boton
                            variante="peligro"
                            onClick={() =>
                              setDecidiendo({ entrada, decision: "write-off" })
                            }
                          >
                            Dar de baja
                          </Boton>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {decididas.length === 0 ? null : (
          <Tarjeta>
            <h2 className="tarjeta__titulo">Decidido en los últimos 30 días</h2>
            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Lote</th>
                    <th className="numero">Cantidad</th>
                    <th>Qué se hizo</th>
                    <th>Quién y cuándo</th>
                    <th>Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {decididas.map((entrada) => (
                    <tr key={entrada.id}>
                      <td>{entrada.productName}</td>
                      <td className="mono">{entrada.lotCode ?? "Sin lote"}</td>
                      <td className="numero">
                        {cantidad(entrada.quantity)} {entrada.unit}
                      </td>
                      <td>
                        <Pastilla
                          texto={estadoDeCuarentena(entrada.status)}
                          tono={
                            entrada.status === "written_off"
                              ? "peligro"
                              : "exito"
                          }
                        />
                      </td>
                      <td className="apagado">
                        {entrada.decidedBy ?? "—"}
                        {entrada.decidedAt === null
                          ? ""
                          : ` · ${dia(entrada.decidedAt)}`}
                      </td>
                      <td className="apagado">{entrada.decisionNote ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Tarjeta>
        )}
      </div>

      {decidiendo === null ? null : (
        <Decidir
          entrada={decidiendo.entrada}
          decision={decidiendo.decision}
          onCerrar={() => setDecidiendo(null)}
        />
      )}
    </>
  );
}

/**
 * La confirmación, con una nota opcional.
 *
 * Confirmar y no un toque directo: dar de baja no se deshace —sale del lote
 * como merma— y liberar pone el frasco a la venta. El `operationId` nace al
 * abrir y se queda: un doble clic o un reintento no libera dos veces.
 */
function Decidir({
  entrada,
  decision,
  onCerrar,
}: {
  entrada: EntradaDeCuarentena;
  decision: "release" | "write-off";
  onCerrar: () => void;
}) {
  const clientes = useQueryClient();
  const [nota, setNota] = useState("");
  const [lote, setLote] = useState("");
  const [vence, setVence] = useState("");
  const [operationId] = useState(newOperationId);
  const libera = decision === "release";
  // El sobrante de un conteo no sabe de qué lote es ni cuándo vence: liberarlo
  // es decirlo. Sin los dos, el botón no se ofrece.
  const pideLote = libera && liberarPideLote(entrada);
  const faltaLote = pideLote && (lote.trim() === "" || vence === "");

  const decidir = useMutation({
    mutationFn: () =>
      almacen.decideQuarantine({
        quarantineId: entrada.id,
        decision,
        operationId,
        note: nota.trim() === "" ? null : nota.trim(),
        ...(pideLote ? { lotCode: lote.trim(), expiresAt: vence } : {}),
      }),
    onSuccess: () => {
      void clientes.invalidateQueries();
      onCerrar();
    },
  });

  return (
    <Modal
      titulo={
        libera
          ? `Liberar ${entrada.productName}`
          : `Dar de baja ${entrada.productName}`
      }
      onCerrar={onCerrar}
    >
      <div className="formulario">
        <p className={libera ? "aviso-caja" : "aviso-caja aviso-caja--aviso"}>
          {pideLote
            ? `Entra al estante: ${cantidad(entrada.quantity)} ${entrada.unit} en el lote y con el vencimiento que digas, al costo del estante. Míralos en la caja: el conteo no los sabía.`
            : entrada.origin === "count_surplus" && !libera
              ? "Se descarta: nunca entró al libro, así que el stock no cambia. Queda anotado aquí con tu nota."
              : entrada.origin === "doubtful_receipt"
                ? libera
                  ? `Entra al estante: ${cantidad(entrada.quantity)} ${entrada.unit} al lote ${entrada.lotCode ?? ""} con el costo de la guía, y se pueden vender.`
                  : "Se devuelve al proveedor: nunca entró al libro, así que el stock no cambia. Queda anotado aquí con tu nota."
              : libera
                ? `Vuelve al estante: ${cantidad(entrada.quantity)} ${entrada.unit} entran al lote ${entrada.lotCode ?? "sin lote"} al costo con que salieron, y se pueden vender.`
                : `Se descarta: el kardex del lote dice que volvió y que salió como merma por devolución de cliente. No se deshace.`}
        </p>
        {pideLote ? (
          <>
            <div className="campo">
              <Rotulo
                htmlFor="lote-cuarentena"
                ayuda="El lote impreso en la caja. Si ya existe, se suma a él."
              >
                Lote
              </Rotulo>
              <input
                id="lote-cuarentena"
                maxLength={60}
                value={lote}
                onChange={(e) => setLote(e.target.value)}
              />
            </div>
            <div className="campo">
              <Rotulo
                htmlFor="vence-cuarentena"
                ayuda="La fecha de vencimiento de la caja. Lo vencido no se libera: se da de baja."
              >
                Vence
              </Rotulo>
              <CampoDeVencimiento
                id="vence-cuarentena"
                valor={vence}
                onCambio={setVence}
                sinFecha={false}
                onSinFecha={() => undefined}
                permiteSinFecha={false}
              />
            </div>
          </>
        ) : null}
        <div className="campo">
          <Rotulo
            htmlFor="nota-cuarentena"
            ayuda="Por qué se decidió así: «precinto intacto», «frasco abierto». Queda con la decisión."
          >
            Nota (opcional)
          </Rotulo>
          <input
            id="nota-cuarentena"
            maxLength={240}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
          />
        </div>
        {decidir.isError ? <Fallo error={decidir.error} /> : null}
        <div className="formulario__fila">
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton
            variante={libera ? "primario" : "peligro"}
            onClick={() => decidir.mutate()}
            disabled={decidir.isPending || faltaLote}
          >
            {decidir.isPending
              ? "Guardando…"
              : libera
                ? "Liberar al estante"
                : "Dar de baja"}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
